import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from 'src/app/core/api/api.service';
import { ClientConfigurationService } from 'src/app/core/client-configuration/client-configuration.service';
import { endpoints } from 'src/app/core/config/endpoints';
import { DateHelperService } from 'src/app/core/helpers/date-helper/date-helper.service';
// tslint:disable-next-line: max-line-length
import { InventoryAttribute, InventoryItem, InventoryRemovalReason, InventoryRemovalUpdate, InventoryStatus } from 'src/app/core/models/inventory.model';
import { AppointmentService } from 'src/app/core/services/appointment/appointment.service';
import { LocalStorageService } from 'src/app/core/storage/local-storage/local-storage.service';
import { ConfigSettings } from '../../client-configuration/config-settings.enum';
import { AppointmentInventory } from '../../models/appointment-inventory.model';
import { Appointment } from '../../models/appointment.model';

@Injectable({
	providedIn: 'root'
})
export class InventoryService {
	constructor(
		private storage: LocalStorageService,
		private apiService: ApiService,
		private dateHelper: DateHelperService,
		private appointmentService: AppointmentService,
		private clientConfig: ClientConfigurationService,
	) { }

	async getAddInventoryModalConfig() {
		return {
			canToggle: await this.canToggleForm(),
			useTruck: await this.shouldDefaultToTruck(),
		};
	}

	async getAllInventoryStatuses(): Promise<InventoryStatus[]> {
		return await this.storage.inventoryStatuses.getAll();
	}

	async canToggleForm(): Promise<boolean> {
		const setting = (await this.clientConfig.getSetting(ConfigSettings.Allow_Inventory_Mode_Switch));
		if (!setting) {
			return false;
		}

		return (setting.value === 'True');
	}

	async shouldDefaultToTruck(): Promise<boolean> {
		const setting = (await this.clientConfig.getSetting(ConfigSettings.UseTruck));
		return !!setting?.value;
	}

	async validateInventory(equipmentId: number, attributeId: number, attributeValue: string): Promise<Observable<any>> {
		const url = endpoints.inventory.warehouse.equipment.validate
			.replace(':equipId', `${equipmentId}`)
			.replace(':attrId', `${attributeId}`);

		return this.apiService.get(url, {
			params: {
				attributeValue,
			},
		});
	}

	async getRemovalReasons(): Promise<InventoryRemovalReason[]> {
		const removalReasons = await this.storage.inventoryRemovalReasons.getAll();
		return removalReasons.sort((a, b) => a.id - b.id);
	}

	async getRemovalStatuses(): Promise<InventoryStatus[]> {
		const inventoryStatuses = await this.storage.inventoryStatuses.getAll() as InventoryStatus[];
		const removalStatuses = inventoryStatuses.filter(s => s.isForEquipmentToBeRemoved);
		return removalStatuses.sort((a, b) => a.status > b.status ? 1 : -1);
	}

	async updateInventoryAttributes(apptId: number, inventoryId: number, updatedAttributes: InventoryAttribute[]) {
		const url = endpoints.appointment.inventory.updateAttributes
			.replace(':id', `${apptId}`)
			.replace(':invId', `${inventoryId}`);

		const body = {
			apptId,
			inventoryId,
			updatedAttributes
		};
		return this.apiService.put(url, body);
	}

	addInventoryToAppointmentHttp(apptId: number, inventoryItem: InventoryItem, updatedAttributes: InventoryAttribute[]) {
		const inventoryId = inventoryItem.id;
		const customerServiceId = inventoryItem.customerServiceId;
		if (inventoryId === 0) {
			return this.addNewInventoryToAppointmentHttp(apptId, inventoryItem);
		}

		const url = endpoints.appointment.inventory.add
			.replace(':id', `${apptId}`)
			.replace(':invId', `${inventoryId}`);

		const body = {
			apptId,
			inventoryId,
			updatedAttributes,
			customerServiceId
		};
		return this.apiService.put(url, body);
	}

	addNewInventoryToAppointmentHttp(apptId: number, inventoryItem: InventoryItem) {
		const url = endpoints.appointment.inventory.new
			.replace(':id', `${apptId}`);

		return this.apiService.post(url, inventoryItem);
	}

	async addInventoryToAppointmentLocal(apptId: number, inventoryItem: InventoryItem) {
		const appointment = await this.appointmentService.getLocalAppointment(apptId);

		appointment.inventory.push({
			id: inventoryItem.id,
			statusId: 1,
			originalStatusId: inventoryItem.statusId,
			categoryDesc: inventoryItem.categoryDesc,
			equipName: inventoryItem.equipName,
			attributes: inventoryItem.attributes,
			customerServiceId: inventoryItem.customerServiceId,
			customerServiceDescription: inventoryItem.customerServiceDescription
		} as AppointmentInventory);

		await this.appointmentService.saveLocalAppointment(appointment);
		await this.removedToBeAddedInventory(appointment, inventoryItem);
	}

	async removedToBeAddedInventory(appointment: Appointment, truckInventory: InventoryItem) {
		const TO_BE_ADDED_STATUS = 4;
		let inventory = appointment.inventory.find(i =>
			i.statusId == TO_BE_ADDED_STATUS &&
			i.categoryId == truckInventory.categoryId &&
			i.equipId == truckInventory.equipId);
		if (!inventory) {
			inventory = appointment.inventory.find(i =>
				i.statusId == TO_BE_ADDED_STATUS &&
				i.categoryId == truckInventory.categoryId &&
				i.equipId == 0);
        }
		if (inventory) {
			await this.deleteToBeAddedInventory(appointment.id, inventory.id);
		}
	}

	async removeInventoryHttp(apptId: number, inventoryId: number, removalUpdates: InventoryRemovalUpdate) {
		const url = endpoints.appointment.inventory.remove
			.replace(':id', `${apptId}`)
			.replace(':invId', `${inventoryId}`);

		const body = {
			apptId,
			inventoryId,
			...removalUpdates,
			removalTime: this.dateHelper.getNowISO()
		};
		return this.apiService.put(url, body);
	}

	async removeInventoryLocal(apptId: number, inventoryId: number, removalUpdates: InventoryRemovalUpdate) {
		const appointment = await this.appointmentService.getLocalAppointment(apptId);

		const inventoryItem = appointment.inventory.find(i => i.id === inventoryId);
		inventoryItem.originalStatusId = 1;
		inventoryItem.statusId = removalUpdates.statusId;

		await this.appointmentService.saveLocalAppointment(appointment);
	}

	async deleteToBeAddedInventory(apptId: number, inventoryId: number) {
		this.deleteToBeAddedInventoryHttp(apptId, inventoryId);
		await this.deleteToBeAddedInventoryLocal(apptId, inventoryId);
	}

	deleteToBeAddedInventoryHttp(apptId: number, inventoryId: number): Observable<any> {
		const url = endpoints.appointment.inventory.deleteToBeAdded
			.replace(':id', `${apptId}`)
			.replace(':invId', `${inventoryId}`);

		return this.apiService.delete(url, {});
	}

	async deleteToBeAddedInventoryLocal(apptId: number, inventoryId: number) {
		const appointment = await this.appointmentService.getLocalAppointment(apptId);
		const TO_BE_ADDED_STATUS = 4;
		const foundIndex = appointment.inventory.findIndex(i => i.statusId == TO_BE_ADDED_STATUS && i.id === inventoryId);
		if (foundIndex === -1) {
			// not found
			return;
		}

		appointment.inventory.splice(foundIndex, 1);

		await this.appointmentService.saveLocalAppointment(appointment);
	}

	async setInventoryStatusToActive(apptId: number, inventoryId: number): Promise<void> {
		this.setInventoryStatusToActiveHttp(apptId, inventoryId);
		await this.setInventoryStatusToActiveLocal(apptId, inventoryId);
	}

	setInventoryStatusToActiveHttp(apptId: number, inventoryId: number): Observable<any> {
		const url = endpoints.appointment.inventory.setToActive
			.replace(':id', `${apptId}`)
			.replace(':invId', `${inventoryId}`);

		return this.apiService.post(url, {});
	}

	async setInventoryStatusToActiveLocal(apptId: number, inventoryId: number) {
		const appointment = await this.appointmentService.getLocalAppointment(apptId);
		const foundIndex = appointment.inventory.findIndex(i => i.id === inventoryId);

		const ACTIVE_ID = 1;
		appointment.inventory[foundIndex].statusId = ACTIVE_ID;

		await this.appointmentService.saveLocalAppointment(appointment);
	}

	async addWarehouseInventory(apptId: number, inventoryItem: InventoryItem, attributeUpdates: InventoryAttribute[]) {
		this.addInventoryToAppointmentHttp(apptId, inventoryItem, attributeUpdates);
		await this.addInventoryToAppointmentLocal(apptId, inventoryItem);
	}
}
